import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { syncAccountBalance } from '../lib/accountBalance';
import { logAudit } from '../lib/audit';
import { log, logError, startTimer } from '../lib/logger';
import { trackMetric } from '../lib/metrics';

export type GoalType =
  | 'reserva_emergencia'
  | 'viagem'
  | 'quitar_divida'
  | 'investimento'
  | 'entrada_imovel'
  | 'compra_pessoal'
  | 'outro';

export type GoalPriority = 'high' | 'medium' | 'low';
export type GoalStatus = 'active' | 'paused' | 'completed';

export interface Goal {
  id: string;
  user_id: string;
  name: string;
  type: GoalType;
  priority: GoalPriority;
  status: GoalStatus;
  target_amount: number;
  current_amount: number;
  target_date: string | null;
  description: string;
  color: string;
  icon: string;
  is_completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface GoalContribution {
  id: string;
  user_id: string;
  goal_id: string;
  amount: number;
  note: string;
  source_type: 'account' | 'debit_card' | 'manual';
  source_account_id: string | null;
  source_card_id: string | null;
  contribution_date: string | null;
  observation: string | null;
  transaction_id: string | null;
  created_at: string;
}

export type GoalPace = 'completed' | 'on_track' | 'at_risk' | 'behind' | 'overdue' | 'no_date';

export interface GoalCalc {
  progress: number;
  remaining: number;
  monthsRemaining: number | null;
  monthlyNeeded: number | null;
  pace: GoalPace;
}

export const calcGoal = (goal: Goal): GoalCalc => {
  const progress = goal.target_amount > 0
    ? Math.min(100, (goal.current_amount / goal.target_amount) * 100)
    : 0;
  const remaining = Math.max(0, goal.target_amount - goal.current_amount);

  if (goal.status === 'completed' || goal.is_completed || goal.current_amount >= goal.target_amount) {
    return { progress: 100, remaining: 0, monthsRemaining: null, monthlyNeeded: null, pace: 'completed' };
  }

  if (!goal.target_date) {
    return { progress, remaining, monthsRemaining: null, monthlyNeeded: null, pace: 'no_date' };
  }

  const today = new Date();
  const target = new Date(goal.target_date + 'T12:00:00');

  if (target <= today) {
    return { progress, remaining, monthsRemaining: 0, monthlyNeeded: null, pace: 'overdue' };
  }

  const monthsRemaining = Math.max(
    0.5,
    (target.getFullYear() - today.getFullYear()) * 12 + (target.getMonth() - today.getMonth())
  );
  const monthlyNeeded = remaining / monthsRemaining;

  const created = new Date(goal.created_at);
  const totalMonths = Math.max(
    1,
    (target.getFullYear() - created.getFullYear()) * 12 + (target.getMonth() - created.getMonth())
  );
  const elapsedMonths = Math.max(
    0,
    (today.getFullYear() - created.getFullYear()) * 12 + (today.getMonth() - created.getMonth())
  );
  const expectedProgress = totalMonths > 0 ? (elapsedMonths / totalMonths) * 100 : 0;

  let pace: GoalPace;
  if (progress >= expectedProgress * 0.95) pace = 'on_track';
  else if (progress >= expectedProgress * 0.65) pace = 'at_risk';
  else pace = 'behind';

  return { progress, remaining, monthsRemaining, monthlyNeeded, pace };
};

export const useGoals = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Security: clear stale data immediately when user changes or logs out ──
  const prevUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentUserId = user?.id;
    if (prevUserIdRef.current !== currentUserId) {
      if (prevUserIdRef.current !== undefined || currentUserId === undefined) {
        setGoals([]);
        setError(null);
      }
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  const fetchGoals = useCallback(async () => {
    if (!user) {
      setGoals([]);   // Clear stale data on logout
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase
      .from('goals')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (err) setError(err.message);
    else setGoals((data as Goal[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchGoals(); }, [fetchGoals]);

  const createGoal = async (values: Omit<Goal, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return { error: 'Não autenticado' };
    const { error: err } = await supabase.from('goals').insert({ ...values, user_id: user.id });
    if (!err) await fetchGoals();
    return { error: err?.message ?? null };
  };

  const updateGoal = async (id: string, values: Partial<Goal>) => {
    const { error: err } = await supabase
      .from('goals')
      .update({ ...values, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (!err) await fetchGoals();
    return { error: err?.message ?? null };
  };

  const deleteGoal = async (id: string) => {
    const { error: err } = await supabase
      .from('goals')
      .delete()
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (!err) await fetchGoals();
    return { error: err?.message ?? null };
  };

  const addContribution = async (
    goalId: string,
    amount: number,
    note: string,
    currentAmount: number,
    options?: {
      sourceType?: 'account' | 'debit_card' | 'manual';
      sourceAccountId?: string | null;
      sourceCardId?: string | null;
      linkedAccountId?: string | null;
      contributionDate?: string;
      observation?: string;
    }
  ) => {
    if (!user) return { error: 'Não autenticado' };
    const elapsed = startTimer();
    log({ flow: 'addContribution', status: 'start', userId: user.id, entity: 'goal_contribution', recordId: goalId, context: { amount } });

    const sourceType = options?.sourceType ?? 'manual';
    const sourceAccountId = options?.sourceAccountId ?? null;
    const sourceCardId = options?.sourceCardId ?? null;
    const linkedAccountId = options?.linkedAccountId ?? null;
    const contributionDate = options?.contributionDate ?? new Date().toISOString().split('T')[0];
    const observation = options?.observation ?? '';

    const newAmount = currentAmount + amount;
    const targetGoal = goals.find(g => g.id === goalId);
    const isCompleted = targetGoal != null && newAmount >= targetGoal.target_amount;

    // 1. Create financial transaction first (so we can store its ID)
    let transactionId: string | null = null;
    const accountIdToDebit = linkedAccountId ?? sourceAccountId;
    if ((sourceType === 'account' || sourceType === 'debit_card') && accountIdToDebit) {
      const goalName = targetGoal?.name ?? 'Meta';
      const { data: txData, error: txErr } = await supabase.from('transactions').insert({
        user_id: user.id,
        account_id: accountIdToDebit,
        card_id: sourceType === 'debit_card' ? sourceCardId : null,
        payment_method: sourceType === 'debit_card' ? 'card' : 'account',
        description: `Aporte: ${goalName}`,
        category: 'Metas',
        type: 'despesa',
        amount,
        date: contributionDate,
        status: 'pago',
        notes: note || observation || '',
        recurrence: 'none',
        installments: 1,
        installment_number: 1,
        installment_group_id: null,
      }).select('id').maybeSingle();

      if (!txErr && txData) {
        transactionId = txData.id;
        await syncAccountBalance(accountIdToDebit);
      }
    }

    // 2. Insert contribution with transaction_id reference
    const { error: cErr } = await supabase.from('goal_contributions').insert({
      user_id: user.id,
      goal_id: goalId,
      amount,
      note,
      source_type: sourceType,
      source_account_id: sourceAccountId,
      source_card_id: sourceCardId,
      contribution_date: contributionDate,
      observation,
      transaction_id: transactionId,
    });
    if (cErr) return { error: cErr.message };

    // 3. Update goal amount
    const { error: uErr } = await supabase
      .from('goals')
      .update({
        current_amount: newAmount,
        is_completed: isCompleted,
        status: isCompleted ? 'completed' : undefined,
        updated_at: new Date().toISOString(),
      })
      .eq('id', goalId)
      .eq('user_id', user.id);
    if (!uErr) {
      log({ flow: 'addContribution', status: 'success', userId: user.id, entity: 'goal_contribution', recordId: goalId, message: 'Aporte registrado', context: { amount, isCompleted }, durationMs: elapsed() });
      trackMetric('add_goal_contribution', { flow: 'addContribution', success: true, durationMs: elapsed() });
      await fetchGoals();
    } else {
      logError('addContribution', `Erro ao atualizar meta: ${uErr.message}`, { userId: user.id, recordId: goalId, durationMs: elapsed() });
      trackMetric('add_goal_contribution', { flow: 'addContribution', success: false, durationMs: elapsed() });
    }
    return { error: uErr?.message ?? null };
  };

  const removeContribution = async (contribution: GoalContribution) => {
    if (!user) return { error: 'Não autenticado' };

    const goal = goals.find(g => g.id === contribution.goal_id);
    if (!goal) return { error: 'Meta não encontrada' };

    let accountToSync: string | null = null;

    // 1. Delete linked financial transaction
    if (contribution.source_type !== 'manual') {
      if (contribution.transaction_id) {
        // Reliable path: soft-delete by exact transaction ID
        const { data: txData } = await supabase
          .from('transactions')
          .select('account_id')
          .eq('id', contribution.transaction_id)
          .eq('user_id', user.id)
          .maybeSingle();
        accountToSync = txData?.account_id ?? contribution.source_account_id ?? null;

        // Soft-delete: preserva o registro para auditoria, consistente com o resto do sistema
        await supabase
          .from('transactions')
          .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
          .eq('id', contribution.transaction_id)
          .eq('user_id', user.id);
      } else {
        // Fallback for old contributions without transaction_id: match by criteria
        accountToSync = contribution.source_account_id ?? null;
        if (accountToSync) {
          const contribDate = contribution.contribution_date ?? contribution.created_at.split('T')[0];
          const { data: txMatch } = await supabase
            .from('transactions')
            .select('id')
            .eq('user_id', user.id)
            .eq('account_id', accountToSync)
            .eq('amount', contribution.amount)
            .eq('type', 'despesa')
            .eq('status', 'pago')
            .ilike('description', 'Aporte:%')
            .eq('date', contribDate)
            .is('deleted_at', null)
            .limit(1);
          if (txMatch && txMatch.length > 0) {
            // Soft-delete: consistente com o padrão do sistema
            await supabase
              .from('transactions')
              .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
              .eq('id', txMatch[0].id)
              .eq('user_id', user.id);
          }
        }
      }
    }

    // 2. Soft-delete contribution record (preserva histórico para auditoria)
    const { error: cErr } = await supabase
      .from('goal_contributions')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', contribution.id)
      .eq('user_id', user.id);
    if (cErr) return { error: cErr.message };

    // ── Audit trail ──────────────────────────────────────────────────────
    logAudit({
      action: 'delete_goal_contribution',
      resource: 'goal_contribution',
      resource_id: contribution.id,
      details: { goal_id: contribution.goal_id, amount: contribution.amount },
    });

    // 3. Update goal current_amount
    const newAmount = Math.max(0, goal.current_amount - contribution.amount);
    const shouldReactivate = (goal.is_completed || goal.status === 'completed') && newAmount < goal.target_amount;
    const { error: uErr } = await supabase
      .from('goals')
      .update({
        current_amount: newAmount,
        is_completed: newAmount >= goal.target_amount,
        status: shouldReactivate ? 'active' : goal.status,
        updated_at: new Date().toISOString(),
      })
      .eq('id', goal.id)
      .eq('user_id', user.id);

    // 4. Sync account balance
    if (accountToSync) {
      await syncAccountBalance(accountToSync);
    }

    if (!uErr) await fetchGoals();
    return { error: uErr?.message ?? null };
  };

  const fetchContributions = async (goalId: string): Promise<GoalContribution[]> => {
    if (!user) return [];
    const { data } = await supabase
      .from('goal_contributions')
      .select('*')
      .eq('goal_id', goalId)
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    return (data as GoalContribution[]) ?? [];
  };

  return {
    goals, loading, error,
    createGoal, updateGoal, deleteGoal,
    addContribution, removeContribution, fetchContributions,
    refetch: fetchGoals,
  };
};
